import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { User, UserRole, UpdateUser } from '../types/user';
import { userService } from '../services/userService';

interface ModifyEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  user: User | null;
}

const userRoles: UserRole[] = ["OWNER", "MANAGER", "OFFICE_ADMIN", "ENGINEER", "SUPERVISOR", "TECHNICIAN"];

const ModifyEmployeeModal: React.FC<ModifyEmployeeModalProps> = ({ open, onClose, onSave, user }) => {
  const [userData, setUserData] = useState<UpdateUser>({});
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setUserData({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        phone: user.phone,
      });
    }
  }, [user]);



  const handleSubmit = async () => {
    if (!user) return;

    setIsSaving(true);
    setError(null);
    try {
      await userService.updateUser(user.id, userData);
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      const errorData = err.response?.data;
      const errorMessage = errorData ? Object.values(errorData).join(' ') : 'An unexpected error occurred.';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Modify Employee Details</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField margin="normal" required fullWidth label="Email Address" name="email" value={userData.email || ''} onChange={(e) => setUserData(prev => ({...prev, email: e.target.value}))} />
          <TextField margin="normal" fullWidth label="First Name" name="first_name" value={userData.first_name || ''} onChange={(e) => setUserData(prev => ({...prev, first_name: e.target.value}))} />
          <TextField margin="normal" fullWidth label="Last Name" name="last_name" value={userData.last_name || ''} onChange={(e) => setUserData(prev => ({...prev, last_name: e.target.value}))} />
          <TextField margin="normal" fullWidth label="Phone" name="phone" value={userData.phone || ''} onChange={(e) => setUserData(prev => ({...prev, phone: e.target.value}))} />
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              name="role"
              value={userData.role || 'TECHNICIAN'}
              label="Role"
              onChange={(e) => setUserData(prev => ({...prev, role: e.target.value as UserRole}))}
            >
              {userRoles.map(role => (
                <MenuItem key={role} value={role}>{role.replace('_', ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModifyEmployeeModal;